import { isEqual } from "lodash";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useInspectDataConfig } from "../../hooks/use-inspect-data";
import { shouldAddPdfExport } from "../helper";
import { inspectData, preview } from "common/constants";
import { ModalName } from "components/modal/types";
import { useWidgetComponentProps } from "components/widgets/hooks/use-current-widget";
import { useCurrentDashboardSection, useCurrentDashboardType } from "components/workspaces/hooks/use-current-dashboard";
import { useGetEntityInfo } from "components/workspaces/hooks/use-get-dashboards-by-type";
import { appInsightEvents, language } from "language/default";
import { appInsightsTrackEvent } from "plugins/app-insight-init";
import { widgetSelector } from "store/cross-slice/widgetSelector";
import { openModal } from "store/modal";
import { PdfModalType } from "store/modal/pdf-modal-types";
import { getCurrentLocalTime } from "common/date-utils";
export let exportInspectDataItem = {};
export const findItemIndex = (dataMenu, i) => (dataMenu.length === 0
    ? -1
    : dataMenu[dataMenu.length - 1].findIndex((menuItem) => menuItem.key === i.key));
export const addOrUpdateDataMenu = (params) => {
    const { i, componentName, widgetWithoutData, sectionId, mode, widgetCompProps, widgetId, itemIndex, dataMenu } = params;
    if (shouldAddPdfExport(componentName, widgetWithoutData, sectionId, mode, Boolean(widgetCompProps.disablePdfExport)) &&
        i.key === `${sectionId}-${widgetId}-export-inspect-data-menu-item`) {
        exportInspectDataItem = i;
    }
    else if (itemIndex === -1) {
        dataMenu[dataMenu.length - 1]?.push(i);
    }
    else {
        dataMenu[dataMenu.length - 1][itemIndex] = i;
    }
};
export const useGetDataMenu = (props) => {
    const { dataMenu: childDataMenu, showMaximize, widgetId, sectionId, mode } = props, componentName = useSelector((state) => widgetSelector(state, widgetId || "")?.componentName), isInspectDataWidget = sectionId === inspectData.sectionId && widgetId === inspectData.widgetId, isPreviewMode = sectionId === preview.sectionId && !isInspectDataWidget;
    const inspectDataMenuItems = useInspectDataConfig({ sectionId, widgetId });
    const entityInfo = useGetEntityInfo();
    const selectedSectionId = useCurrentDashboardSection(), selectedType = useCurrentDashboardType(), templateAnalysis = useSelector((state) => state.entities.Template?.[selectedType]?.analysis ??
        state.entities.Template?.[(entityInfo.type ?? "").concat(entityInfo.subtype ?? "")]?.analysis, isEqual);
    const dispatch = useDispatch();
    const widgetWithoutData = useSelector((state) => state.widgetsWithoutData?.[widgetId || ""], isEqual);
    const widgetCompProps = useWidgetComponentProps(sectionId, widgetId);
    const memoizedDataMenu = useMemo(() => {
        const clonedChildDataMenu = childDataMenu?.map((menuGroup) => [...menuGroup]) ?? [[]];
        if (clonedChildDataMenu.length === 0) {
            clonedChildDataMenu.push([]);
        }
        if (inspectDataMenuItems?.length) {
            inspectDataMenuItems.forEach((i) => {
                const itemIndex = findItemIndex(clonedChildDataMenu, i);
                addOrUpdateDataMenu({ componentName, dataMenu: clonedChildDataMenu, i, itemIndex, mode, sectionId, widgetCompProps, widgetId, widgetWithoutData });
            });
        }
        if (shouldAddPdfExport(componentName, widgetWithoutData, sectionId, mode, Boolean(widgetCompProps.disablePdfExport))) {
            const pdfMenuKey = `${sectionId}-${widgetId}-download-pdf-menu-item`;
            const pdfMenuGroup = [
                ...Object.keys(exportInspectDataItem ?? {}).length > 0 ? [exportInspectDataItem] : [],
                {
                    action: () => {
                        const currentAnalysisIndex = templateAnalysis?.findIndex((al) => al.sections?.find((se) => se.id === selectedSectionId)) ?? -1;
                        const analysisName = templateAnalysis?.[currentAnalysisIndex].name;
                        dispatch(openModal({ content: {
                                id: `${sectionId}-${widgetId}-export-pdf-modal`,
                                modalName: ModalName.DownloadPdfModal,
                                parameters: {
                                    analysesList: templateAnalysis,
                                    analysis: analysisName,
                                    analysisIndex: currentAnalysisIndex,
                                    expanded: !isPreviewMode && showMaximize,
                                    mode: PdfModalType.Widget,
                                    widgetId
                                }
                            },
                            title: language.en.dashboard.pdfExport.downloadPdf,
                            type: "action" }));
                        appInsightsTrackEvent({ name: appInsightEvents.customEvents.names.WidgetExportToPDF, properties: { LocalTimestamp: getCurrentLocalTime() } });
                    },
                    key: pdfMenuKey,
                    label: language.en.dashboard.pdfExport.downloadPdf,
                    leftIcon: "export"
                }
            ];
            const hasPdfMenuItem = clonedChildDataMenu.some((menuGroup) => menuGroup.some((menuItem) => menuItem.key === pdfMenuKey));
            if (!hasPdfMenuItem) {
                clonedChildDataMenu.push(pdfMenuGroup);
            }
        }
        return clonedChildDataMenu;
    }, [
        childDataMenu,
        componentName,
        dispatch,
        inspectDataMenuItems,
        isPreviewMode,
        mode,
        sectionId,
        selectedSectionId,
        showMaximize,
        templateAnalysis,
        widgetCompProps,
        widgetId,
        widgetWithoutData
    ]);
    return memoizedDataMenu;
};
